// @ts-strict-ignore
import { IsThreeJsType } from '@3d-scene/common/util/is-threejs-type';
import { isArray } from 'lodash';
import { Box3, LineSegments, Material, Matrix4, Mesh, Object3D, Vector3 } from 'three';
import { ArrayUtils } from '@shared/util/array-utils';
import { BaseContainer3D } from '../../model3d/base-container-3d';
import { Connection } from '../../model3d/connection';
import { ArticleContainer } from '../../model3d/article-container';
import { PointsPair } from '../../model/points-pair';

export const MATERIAL_USAGE_COUNT = 'usageCount';
export const MOUSE_ENABLED = 'mouseEnabled';

export class Object3DUtils {
  public static getChildren(object3D: Object3D): Object3D[] {
    const result: Object3D[] = [];
    if (ArrayUtils.isNotEmpty(object3D.children)) {
      for (const child of object3D.children) {
        result.push(child);
      }
    }
    return result;
  }

  public static getChildrenRecursively(object3D: Object3D, excludeDebug3DElements: boolean = true): Object3D[] {
    if (!object3D) {
      return [];
    }
    const result: Object3D[] = [];
    if (!ArrayUtils.isEmpty(object3D.children)) {
      for (const childObject3D of object3D.children) {
        if (childObject3D && (!excludeDebug3DElements || (
          childObject3D.name !== Connection.DEFAULT_CONNECTION_OUTLINE_NAME
          && childObject3D.name !== ArticleContainer.AXISHELPER_NAME))
        ) {
          const children: Object3D[] = this.getChildrenRecursively(childObject3D, excludeDebug3DElements);
          ArrayUtils.addAll(result, children);
          result.push(childObject3D);
        }
      }
    }
    return result;
  }

  /** @returns all materials found in an object and its recursive children */
  public static getObjectsMaterials(container: Object3D): Material[] {
    let materials: Material[] = [];
    container.traverse((item) => {
      if (IsThreeJsType.mesh(item)) {
        const objectsMaterials = isArray(item.material) ? item.material : [item.material];
        materials.push(...objectsMaterials)
      }
    });
    return materials;
  }

  /** @returns the first material found in an objects recursive children */
  public static findFirstMaterial(container: Object3D, excludedNames: string[] = null): Material {
    if (!container) {
      return null;
    }
    let material: Material = null;
    const children: Object3D[] = Object3DUtils.getChildrenRecursively(container);
    for (const childContainer of children) {
      if (excludedNames && excludedNames.indexOf(childContainer.name) >= 0) {
        continue;
      }
      if (IsThreeJsType.mesh(childContainer)) {
        material = childContainer.material as Material;
        break;
      } else {
        material = this.findFirstMaterial(childContainer, excludedNames);
        if (material) {
          break;
        }
      }
    }
    return material;
  }

  public static changeChildrenVisibilityByName(container: Object3D, childName: string, visible: boolean): boolean {
    let visibilityChanged: boolean = false;
    for (const child of this.getChildrenRecursively(container)) {
      if (child.name === childName) {
        child.visible = visible;
        visibilityChanged = true;
      } else {
        visibilityChanged = visibilityChanged || this.changeChildrenVisibilityByName(child, childName, visible);
      }
    }
    return visibilityChanged;
  }

  public static getChildByName(container: Object3D, name: string): Object3D {
    if (!container) {
      return null;
    }

    let searchContainer: Object3D = null;
    for (const childContainer of this.getChildrenRecursively(container)) {
      if (childContainer.name === name) {
        searchContainer = childContainer;
        break;
      } else {
        searchContainer = this.getChildByName(childContainer, name);
        if (searchContainer) {
          break;
        }
      }
    }
    return searchContainer;
  }

  /**
   * This method will destroy object and all descendants. Be careful, because it will also destroy geometries that can be shared by other
   * objects.
   * If 'disposeMaterials' is set to 'true' it will destroy materials and their textures.
   * @param object3D
   * @param disposeMaterials
   */
  public static destroy(object3D: Object3D, disposeMaterials: boolean = true): void {
    if (!object3D) {
      return;
    }

    while (!ArrayUtils.isEmpty(object3D.children)) {
      Object3DUtils.destroy(object3D.children[0], disposeMaterials);
    }

    if (IsThreeJsType.mesh(object3D)) {
      const mesh: Mesh = object3D;
      mesh.geometry?.dispose();

      const material: Material = mesh.material as Material;
      if (disposeMaterials && material) {
        this.disposeMaterial(material);
      }
      mesh.geometry = undefined;
      mesh.material = undefined;
    }

    if (IsThreeJsType.lineSegments(object3D)) {
      const lineSegments: LineSegments = object3D;
      lineSegments.geometry?.dispose();

      const material: Material = lineSegments.material as Material;
      if (disposeMaterials && material) {
        this.disposeMaterial(material);
      }
      lineSegments.geometry = undefined;
      lineSegments.material = undefined;
    }

    if (object3D instanceof BaseContainer3D) {
      object3D.dispose();
    }

    if (object3D.parent) {
      object3D.parent.remove(object3D);
    }
  }

  private static disposeMaterial(material: Material): void {
    if (IsThreeJsType.meshPhongMaterial(material) ||
      IsThreeJsType.meshBasicMaterial(material) ||
      IsThreeJsType.meshLambertMaterial(material)) {

      if (material.map) {
        material.map.dispose();
        material.map = undefined;
      }
    }
    material.dispose();
  }

  public static disposeAllChildren(object3D: Object3D): void {
    if (!object3D) {
      return;
    }
    while (!ArrayUtils.isEmpty(object3D.children)) {
      Object3DUtils.dispose(object3D.children[0]);
    }
  }

  /**
   * This method will dispose Object3D and descendant objects without disposing the geometry. It is save to use this method if other
   * objects use the same geometry instance.
   * If 'disposeMaterials' is set to 'true' it will destroy materials and their textures.
   * @param object3D
   * @param disposeMaterials
   */
  public static dispose(object3D: Object3D, disposeMaterials: boolean = true): void {
    if (!object3D) {
      return;
    }

    if (object3D.parent) {
      object3D.parent.remove(object3D);
    }
    if (IsThreeJsType.mesh(object3D)) {
      const mesh: Mesh = object3D;
      const material: Material = mesh.material as Material;
      mesh.material = null;
      this.updateMaterialUsageCount(material);
      if (disposeMaterials && material && !this.isMaterialShared(material)) {
        this.disposeMaterial(material);
      }
    }

    // the parent has to be disposed before his children, so that the parent has the first crack at disposing his kind
    if (object3D instanceof BaseContainer3D) {
      object3D.dispose();
    }
    while (!ArrayUtils.isEmpty(object3D.children)) {
      Object3DUtils.dispose(object3D.children[0], disposeMaterials);
    }
  }

  private static updateMaterialUsageCount(material: Material): void {
    if (!material) {
      return;
    }
    if (material.hasOwnProperty(MATERIAL_USAGE_COUNT)) {
      material[MATERIAL_USAGE_COUNT]--;
    }
  }

  private static isMaterialShared(material: Material): boolean {
    // Usage count '0' means that all objects that used this material were deleted/disposed
    // and only one instance left (probably in the cache of 'ArticleObject3DManager')
    if (material.hasOwnProperty(MATERIAL_USAGE_COUNT)) {
      return material[MATERIAL_USAGE_COUNT] >= 0;
    }
    return false;
  }

  public static getAxisAlignedBoundingBoxForObjectContainer(container: Object3D, excludedObjectNames: string[] = null): Box3 {
    if (!container) {
      return null;
    }
    if (ArrayUtils.isEmpty(container.children)) {
      return new Box3();
    }
    let points: Vector3[] = this.collectBoundingPoints(container, excludedObjectNames);
    if (ArrayUtils.isEmpty(points)) {
      // try to retrieve bounding box by connections in case model doesn't contain other geometry
      points = this.collectBoundingPoints(container, null);
    }
    return new Box3().setFromPoints(points);
  }

  private static collectBoundingPoints(container: Object3D, excludedObjectNames: string[] = null): Vector3[] {
    const points: Vector3[] = [];
    for (const object of container.children) {
      if (IsThreeJsType.mesh(object) && (!excludedObjectNames || (excludedObjectNames && excludedObjectNames.indexOf(object.name) === -1))) {
        object.geometry.computeBoundingBox();
        const boundingBox: Box3 = object.geometry.boundingBox.clone();
        boundingBox.applyMatrix4(object.matrix);
        points.push(boundingBox.min, boundingBox.max);
      }
    }
    return points;
  }

  public static getAxisAlignedBoundingBoxByMinMax(min: Vector3, max: Vector3): Box3 {
    return new Box3().setFromPoints([min, max]);
  }

  public static transformBoundsPoints(bounds: Box3, transformMatrix: Matrix4): Box3 {
    if (!bounds || !transformMatrix) {
      return null;
    }
    return bounds.clone().applyMatrix4(transformMatrix);
  }

  public static transformBounds(bounds: Box3, transformMatrix: Matrix4): Box3 {
    if (!bounds || !transformMatrix) {
      return bounds;
    }
    return bounds.clone().applyMatrix4(transformMatrix);
  }

  public static extractBoundingPoints(bounds: Box3): Vector3[] {
    if (!bounds) {
      return [];
    }

    const max: Vector3 = bounds.max;
    const min: Vector3 = bounds.min;
    const aabbPoints: Vector3[] = [];

    /*
      5____4
     1/___0/|
     | 6__|_7
     2/___3/
    */

    aabbPoints.push(new Vector3(max.x, max.y, max.z));
    aabbPoints.push(new Vector3(min.x, max.y, max.z));
    aabbPoints.push(new Vector3(min.x, min.y, max.z));
    aabbPoints.push(new Vector3(max.x, min.y, max.z));
    aabbPoints.push(new Vector3(max.x, max.y, min.z));
    aabbPoints.push(new Vector3(min.x, max.y, min.z));
    aabbPoints.push(new Vector3(min.x, min.y, min.z));
    aabbPoints.push(new Vector3(max.x, min.y, min.z));

    return aabbPoints;
  }

  public static moveTransformationToGeometry(object: Object3D, transformationMatrix?: Matrix4): void {
    if (!object) {
      return;
    }
    object.updateMatrix();
    if (IsThreeJsType.mesh(object)) {
      object.geometry = object.geometry.clone();
      const matrixToApply: Matrix4 = transformationMatrix
        ? new Matrix4().multiplyMatrices(transformationMatrix, object.matrix)
        : new Matrix4().copy(object.matrix);
      object.geometry.applyMatrix4(matrixToApply);
      object.rotation.set(0, 0, 0);
      object.position.set(0, 0, 0);
      object.scale.set(1, 1, 1);
      object.updateMatrix();
    }
    if (object.children.length) {
      transformationMatrix = transformationMatrix
        ? new Matrix4().multiplyMatrices(transformationMatrix, object.matrix)
        : new Matrix4().copy(object.matrix);
      object.position.set(0, 0, 0);
      object.rotation.set(0, 0, 0);
      object.scale.set(1, 1, 1);
      object.updateMatrix();
      object.children.forEach((ch: any) => Object3DUtils.moveTransformationToGeometry(ch, new Matrix4().copy(transformationMatrix)));
    }
  }

  public static hideInGeometryRepresentation(...objects: Object3D[]) {
    for (const object3D of objects) {
      if(object3D) {
        object3D.userData.hiddenInGeometryRepresentation = true;
      }
    }
  }

  public static isHiddenInGeometryRepresentation(object: Object3D) {
    return !!object.userData?.hiddenInGeometryRepresentation;
  }

  /**
   * Checks if the given object or any of its parents is hidden.
   * @param object an Object3D to check for hidden state.
   * @returns true if the object is hidden, false otherwise.
   */
  public static isHiddenInScene(object: Object3D): boolean {
    let currentObject: Object3D | null = object;
    while (currentObject) {
      if (this.isHidden(currentObject)) {
        return true;
      }
      currentObject = currentObject.parent;
    }
    return false;
  }

  /**
   * Checks if the given object is hidden.
   * @param object a Mesh to check for hidden state.
   * @returns true if the object is hidden, false otherwise.
   */
  public static isHidden(object: Object3D): boolean {
    // Connection-Meshes can have a custom property "mouseEnabled".
    // For context see Connection.updateMouseEnabledForMesh()
    if (object.hasOwnProperty(MOUSE_ENABLED) && !(<any>object)[MOUSE_ENABLED]) {
      return true;
    }

    // as fallback, check visibility of material since some connections (e.g. on label-strip) are hidden by such way
    return !object.visible || this.hasHiddenMaterial(object);
  }

  /**
   * Checks if given the object's materials is hidden.
   * @param object a Mesh to check for hidden materials.
   * @returns true if the object uses hidden material, false otherwise.
   */
  public static hasHiddenMaterial(object: Object3D): boolean {
    if (!IsThreeJsType.mesh(object)) return false;

    const material: Material | Material[] = object.material;
    if (!material) {
      return false;
    }

    if (material instanceof Material) {
      return !material.visible;
    } else if (material instanceof Array) {
      return material.every((m) => !m.visible);
    } else {
      return false;
    }
  }

  /**
   * Method creates point pairs between closest and farthest points.
   * A Point pair is a pair of points, which reside on different connections/articles, for example
   * <pre>
   * |a1   a4|  |b1   b4|
   * |a2   a3|  |b2   b3|
   * </pre>
   * The farthest pairs will be (a1, b3) and (a2, b4). First point in pair will be point from closest points,
   * and second one will be point from farthest points.
   *
   * @param closestPoints closest points
   * @param farestPoints farthest points
   * @return point pairs; pairs from result array will be pairs between the most distant points
   *
   */
  public static findPointPairs(closestPoints: Vector3[], farestPoints: Vector3[]): PointsPair[] {
    const pointPairs: PointsPair[] = [];

    for (const closestPoint of closestPoints) {
      for (const farestPoint of farestPoints) {
        const distance: number = closestPoint.distanceTo(farestPoint);
        pointPairs.push(new PointsPair(closestPoint, farestPoint, distance));
      }
    }

    pointPairs.sort((a: PointsPair, b: PointsPair) => b.distance - a.distance);

    const usedPoints: Vector3[] = [];
    const resultPairs: PointsPair[] = [];
    for (const pair of pointPairs) {
      if (!usedPoints.includes(pair.startPoint) && !usedPoints.includes(pair.endPoint)) {
        usedPoints.push(pair.startPoint, pair.endPoint);
        resultPairs.push(pair);
      }
    }

    return resultPairs;
  }
}
